// @flow

import React, { useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFormikContext } from 'formik';

import { EstimateContext } from '../../../contexts/EstimateContext';
import {
  HEADER_SUFFIX_FOR_DOLLAR_COLUMNS,
  NATIVE_INFERENCE_POOL_ENABLED,
  NATIVE_INFERENCE_POOL_ENABLED_BACKEND_NAME_FOR_SUMMARY,
  NATIVE_INFERENCE_POOL_ID,
  NATIVE_INFERENCE_POOL_ID_BACKEND_NAME,
  NATIVE_INFERENCE_POOL_NAME,
  NATIVE_INFERENCE_POOL_NAME_BACKEND_NAME,
  NATIVE_INFERENCE_POOL_PROVIDER,
  NATIVE_INFERENCE_POOL_PROVIDER_BACKEND_NAME,
  NATIVE_INFERENCE_POOL_REGION,
  NATIVE_INFERENCE_POOL_REGION_BACKEND_NAME,
  NATIVE_INFERENCE_POOL_TOTAL_SPEND_AT_LIST,
  NATIVE_INFERENCE_POOL_TOTAL_SPEND_AT_LIST_BACKEND_NAME,
  NATIVE_INFERENCE_POOL_TOTAL_SPEND_DISCOUNTED,
  NATIVE_INFERENCE_POOL_TOTAL_SPEND_DISCOUNTED_BACKEND_NAME,
} from '../../../constants';
import { ENABLED_DISABLED_FILTER_PARAMS } from '../grid-utils/constants';
import { CurrencyZeroDPRenderer } from '../grid-utils/renderers';
import { PaddedAndRaisedSegment } from '../../presentational/PaddedAndRaisedSegment';
import { PrimaryButton } from '../../presentational/buttons/PrimaryButton';
import { DataGrid } from '../grid-utils/DataGrid';
import { isAnythingEditedOnEstimatePage } from '../utils';

import { NativeInferencePoolEnabledCellRenderer } from './NativeInferencePoolEnabledCellRenderer';
import { NativeInferencePoolCopyRenderer } from './NativeInferencePoolCopyRenderer';
import { NativeInferencePoolDeleteRenderer } from './NativeInferencePoolDeleteRenderer';
import { NativeInferencePoolAddModal } from './NativeInferencePoolAddModal';
import { NativeInferencePoolNameRenderer } from './NativeInferencePoolNameRenderer';

export const NativeInferenceSummaryContainer = () => {
  const { estimateId } = useParams();
  const [isNativeInferencePoolAddModalOpen, setNativeInferencePoolAddModalOpen] = useState(false);
  const { values, initialValues, isValid } = useFormikContext();
  const estimate = useContext(EstimateContext);

  const columnDefs = [
    {
      field: NATIVE_INFERENCE_POOL_ID,
      backEndFieldName: NATIVE_INFERENCE_POOL_ID_BACKEND_NAME,
      hide: true,
    },
    {
      field: NATIVE_INFERENCE_POOL_NAME,
      backEndFieldName: NATIVE_INFERENCE_POOL_NAME_BACKEND_NAME,
      cellRenderer: NativeInferencePoolNameRenderer,
    },
    {
      field: NATIVE_INFERENCE_POOL_ENABLED,
      filter: true,
      filterParams: ENABLED_DISABLED_FILTER_PARAMS,
      cellRenderer: NativeInferencePoolEnabledCellRenderer,
      backEndFieldName: NATIVE_INFERENCE_POOL_ENABLED_BACKEND_NAME_FOR_SUMMARY,
    },
    {
      field: NATIVE_INFERENCE_POOL_PROVIDER,
      backEndFieldName: NATIVE_INFERENCE_POOL_PROVIDER_BACKEND_NAME,
    },
    {
      field: NATIVE_INFERENCE_POOL_REGION,
      backEndFieldName: NATIVE_INFERENCE_POOL_REGION_BACKEND_NAME,
      tooltipField: NATIVE_INFERENCE_POOL_REGION,
    },
    {
      field: NATIVE_INFERENCE_POOL_TOTAL_SPEND_AT_LIST,
      backEndFieldName: NATIVE_INFERENCE_POOL_TOTAL_SPEND_AT_LIST_BACKEND_NAME,
      cellRenderer: CurrencyZeroDPRenderer,
      headerSuffix: HEADER_SUFFIX_FOR_DOLLAR_COLUMNS,
    },
    {
      field: NATIVE_INFERENCE_POOL_TOTAL_SPEND_DISCOUNTED,
      backEndFieldName: NATIVE_INFERENCE_POOL_TOTAL_SPEND_DISCOUNTED_BACKEND_NAME,
      cellRenderer: CurrencyZeroDPRenderer,
      headerSuffix: HEADER_SUFFIX_FOR_DOLLAR_COLUMNS,
    },
    {
      field: '',
      filter: false,
      cellRenderer: NativeInferencePoolCopyRenderer,
      suppressSizeToFit: true,
      maxWidth: 60,
      sortable: false,
    },
    {
      field: '',
      filter: false,
      cellRenderer: NativeInferencePoolDeleteRenderer,
      suppressSizeToFit: true,
      maxWidth: 60,
      sortable: false,
    },
  ];

  const disabled = isAnythingEditedOnEstimatePage(values, initialValues);

  const inputDataSource = estimate?.native_inference_pool_summary ?? [];
  const areAnyNativeInferencePoolsConfigured = inputDataSource.length > 0;

  return (
    <PaddedAndRaisedSegment disabled={disabled}>
      <PrimaryButton
        disabled={disabled || !isValid || !estimateId}
        onClick={() => {
          setNativeInferencePoolAddModalOpen(true);
        }}
        size="mini"
        text="Add Inference Pool"
      />

      <NativeInferencePoolAddModal
        isOpen={isNativeInferencePoolAddModalOpen}
        setOpen={setNativeInferencePoolAddModalOpen}
      />

      {areAnyNativeInferencePoolsConfigured && (
        <DataGrid
          columnDefs={columnDefs}
          downloadAllCols={false}
          inputDataSource={inputDataSource}
          label="Native Inference Summary"
          sizeColumnsToFitInitially={true}
          tooltipShowDelay={0}
        />
      )}
    </PaddedAndRaisedSegment>
  );
};
